import { Workbook, Worksheet } from 'exceljs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ColIcon from '@assets/images/csv-col-icon.svg?react'

import { ProjectFile } from '@types'

import { Error as DivError, Item, Label, List } from './CsvViewer.styled'

type CsvViewerProps = {
  file: ProjectFile
  onColSelected: (col?: string[]) => void
}

type FormattedColumns = {
  [key: string]: string[]
}

export const CsvViewer: React.FC<CsvViewerProps> = ({ file, onColSelected }: CsvViewerProps) => {
  const { t } = useTranslation()

  const [headers, setHeaders] = useState<string[]>([])
  const [formattedColumns, setFormattedColumns] = useState<FormattedColumns>({})
  const [isError, setIsError] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  useEffect(() => {
    const fetchAndParseExcel = async () => {
      const findHeaderRow = (worksheet: Worksheet): number => {
        let rowNum = 1
        let headerFound = false

        do {
          const row = worksheet.getRow(rowNum)

          if (row.cellCount > 0) {
            headerFound = true
            break
          }

          rowNum++
        } while (rowNum <= worksheet.rowCount && !headerFound)

        return headerFound ? rowNum : -1
      }

      try {
        const response = await fetch(file.url)
        const arrayBuffer = await response.arrayBuffer()

        const workbook = new Workbook()
        await workbook.xlsx.load(arrayBuffer)

        const worksheet = workbook.worksheets[0]

        if (worksheet) {
          // Trouve la ligne d'en-têtes
          const headerRowIndex = findHeaderRow(worksheet)
          if (headerRowIndex === -1) {
            throw new Error('Aucun en-tête valide trouvé')
          }

          const headerRow = worksheet.getRow(headerRowIndex)
          const headers = headerRow.values as string[]

          const filteredHeaders = headers.slice(1).filter((header) => header !== undefined && header !== '')

          setHeaders(filteredHeaders)

          const columns: FormattedColumns = {}
          filteredHeaders.forEach((header) => {
            columns[header] = []
          })

          // Parcourt les lignes après les en-têtes
          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > headerRowIndex) {
              filteredHeaders.forEach((header, index) => {
                const cellValue = row.getCell(index + 1).value
                const stringValue = cellValue?.toString().trim() || ''

                if (stringValue !== '' && !columns[header].includes(stringValue)) {
                  columns[header].push(stringValue)
                }
              })
            }
          })

          setFormattedColumns(columns)
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error)
        setIsError(true)
      }
    }

    if (file?.url) {
      fetchAndParseExcel()
    }
  }, [file])

  const handleColSelected = (header: string, index: number) => {
    if (index === selectedIndex) {
      onColSelected()
      setSelectedIndex(null)
    } else {
      setSelectedIndex(index)
      onColSelected(formattedColumns[header])
    }
  }

  if (isError) return <DivError>{t('modules_project-form-models_components_CsvViewer_error')}</DivError>

  return (
    <List>
      {headers.map((header, index) => (
        <Item key={header} selected={index === selectedIndex} onClick={() => handleColSelected(header, index)}>
          <ColIcon />
          <Label>{header}</Label>
        </Item>
      ))}
    </List>
  )
}
